import { overlay, Overlay, OverlayOptions } from '../overlay';
import { createNode } from '@opten/dom/create-node';
import { eventNamespaceFactory } from '@opten/events/event-namespace-factory';
import { KEYCODES } from '@opten/keycodes';

import { OverlayPositionBuilder } from '../overlay/position/overlay-position-builder';
import { DialogOptions } from './dialog-options';

// todo: use position strategy from angular material
// todo: create DialogRef

export class Dialog {
	private overlayRef: Overlay;
	private dialogOpen = false;

	private options: DialogOptions = {
		hasBackdrop: true,
		disableClose: false,
		width: '',
		height: '',
		maxWidth: '80vw',
	};

	private eventNamespaceFn = eventNamespaceFactory('opten.dialog');

	constructor(private element: HTMLElement, options: DialogOptions = {}) {
		// todo: way to have data?
		this.options = { ...this.options, ...options };

		this.init();
	}

	private init() {
		Array.from(
			document.querySelectorAll(
				`[href="#${this.element.id}"], [data-target="#${this.element.id}"]`
			)
		).forEach((trigger) =>
			trigger.addEventListener('click', (event) => {
				event.preventDefault();
				this.toggle();
			})
		);
	}

	private open(): void {
		if (this.dialogOpen) {
			return;
		}

		this.dialogOpen = true;
		this.createOverlay().open();

		this.eventNamespaceFn(this.element, 'open');

		this.addListeners();
	}

	private close(): void {
		if (this.dialogOpen) {
			this.dialogOpen = false;

			this.overlayRef.close();

			this.eventNamespaceFn(this.element, 'close');
		}
	}

	private toggle() {
		this.dialogOpen ? this.close() : this.open();
	}

	/**
	 * This method creates the overlay from the provided dialog's template and saves its
	 * OverlayRef so that it can be attached to the DOM when open is called.
	 */
	private createOverlay(): Overlay {
		if (!this.overlayRef) {
			const portalElement = createNode(
				`<div class="c-dialog__container">${this.element.innerHTML}</div>`
			);

			this.overlayRef = overlay(
				portalElement,
				this.getOverlayOptions(this.options)
			);

			this.overlayRef.afterOpened = () =>
				this.eventNamespaceFn(this.element, 'opened');

			this.overlayRef.beforeClose = () => this.overlayRef.detachBackdrop();

			this.overlayRef.afterClosed = () => {
				this.eventNamespaceFn(this.element, 'closed');
				this.overlayRef.dispose();
				this.overlayRef = null;
			};

			if (!this.options.disableClose) {
				this.overlayRef.backdropClick = () => this.close();

				this.overlayRef.onKeyboardEvent = (event) => {
					if (event.keyCode === KEYCODES.ESCAPE) {
						this.close();
					}
				};
			}
		}

		return this.overlayRef;
	}

	private getOverlayOptions(dialogSettings: DialogOptions): OverlayOptions {
		const state = new OverlayOptions({
			// todo: like angular material this._overlayRef.position()?;
			positionStrategy: new OverlayPositionBuilder().global(),
			hasBackdrop: dialogSettings.hasBackdrop,
			minWidth: dialogSettings.minWidth,
			minHeight: dialogSettings.minHeight,
			maxWidth: dialogSettings.maxWidth,
			maxHeight: dialogSettings.maxHeight,
		});

		if (dialogSettings.backdropClassName) {
			state.backdropClassName = dialogSettings.backdropClassName;
		}

		return state;
	}

	private addListeners() {
		if (this.overlayRef && this.overlayRef.portalElement) {
			// todo: we don't have to kill the events, right? because the html gets removed anyway?
			Array.from(
				this.overlayRef.portalElement.querySelectorAll<HTMLElement>(
					'[data-dismiss="dialog"]'
				)
			).forEach((button) =>
				button.addEventListener('click', (event) => {
					event.preventDefault();

					this.close();
				})
			);
		}
	}
}

// TODO: Should we deprecate this?
export function dialog(element: HTMLElement, options?: DialogOptions): Dialog {
	return new Dialog(element, options);
}
